/**
 * 주행 코스의 높이 프로필.
 *
 * 렌더 메시와 차량/카메라가 이 함수를 함께 사용합니다. 각자 경사 좌표를 따로 갖고
 * 있으면 노면을 고친 뒤 차가 뜨거나 파묻히므로, 월드 (x, z)의 높이와 미분을 여기서
 * 단일 진실 공급원으로 둡니다.
 *
 * 출발점에서 +X로 달리면 다음 순서입니다.
 *   120~300m  오르막: 0m -> 18m
 *   300~380m  정상의 평탄부
 *   380~560m  내리막: 18m -> 0m
 *
 * 양 끝은 smootherstep으로 기울기가 0이 되도록 연결합니다. 휠베이스보다 훨씬 긴
 * 전이라 앞·뒤 바퀴가 서로 다른 급경사에 걸려 차체가 노면을 파고드는 일도 없습니다.
 */

export const ELEVATION_COURSE_UPHILL_START_X = 120;
export const ELEVATION_COURSE_UPHILL_END_X = 300;
export const ELEVATION_COURSE_DOWNHILL_START_X = 380;
export const ELEVATION_COURSE_DOWNHILL_END_X = 560;
export const ELEVATION_COURSE_HEIGHT = 18;

/** 흰 경계선 사이의 평평한 주행로 폭은 24m입니다. */
export const ELEVATION_COURSE_ROAD_HALF_WIDTH = 12;
/** 주행로 바깥은 이 지점까지 완만한 사면으로 지면에 이어집니다. */
export const ELEVATION_COURSE_TERRAIN_HALF_WIDTH = 72;

export interface DrivingTerrainSample {
  /** 차량 접지면의 월드 Y (m). */
  height: number;
  /** dh/dx. 표면 법선과 차량 경사 자세를 만드는 데 씁니다. */
  slopeX: number;
  /** dh/dz. 주행로 옆 사면에서 차량이 기울어지는 정도입니다. */
  slopeZ: number;
}

/** 양 끝에서 값뿐 아니라 1차·2차 미분까지 0이 되는 5차 smoothstep입니다. */
function smootherstep(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** 위 smootherstep을 t로 미분한 값입니다. */
function smootherstepDerivative(t: number): number {
  return 30 * t * t * (t - 1) * (t - 1);
}

function curveProgress(value: number, start: number, end: number): number {
  if (value <= start) {
    return 0;
  }
  if (value >= end) {
    return 1;
  }
  return smootherstep((value - start) / (end - start));
}

function curveDerivative(value: number, start: number, end: number): number {
  if (value <= start || value >= end) {
    return 0;
  }
  const span = end - start;
  return smootherstepDerivative((value - start) / span) / span;
}

function longitudinalHeight(x: number): number {
  if (x < ELEVATION_COURSE_UPHILL_END_X) {
    return (
      curveProgress(x, ELEVATION_COURSE_UPHILL_START_X, ELEVATION_COURSE_UPHILL_END_X) *
      ELEVATION_COURSE_HEIGHT
    );
  }
  if (x <= ELEVATION_COURSE_DOWNHILL_START_X) {
    return ELEVATION_COURSE_HEIGHT;
  }
  return (
    (1 - curveProgress(x, ELEVATION_COURSE_DOWNHILL_START_X, ELEVATION_COURSE_DOWNHILL_END_X)) *
    ELEVATION_COURSE_HEIGHT
  );
}

function longitudinalSlope(x: number): number {
  if (x < ELEVATION_COURSE_UPHILL_END_X) {
    return (
      curveDerivative(x, ELEVATION_COURSE_UPHILL_START_X, ELEVATION_COURSE_UPHILL_END_X) *
      ELEVATION_COURSE_HEIGHT
    );
  }
  if (x <= ELEVATION_COURSE_DOWNHILL_START_X) {
    return 0;
  }
  return (
    -curveDerivative(x, ELEVATION_COURSE_DOWNHILL_START_X, ELEVATION_COURSE_DOWNHILL_END_X) *
    ELEVATION_COURSE_HEIGHT
  );
}

/** 주행로 중앙은 평평하게 두고, 바깥 60m에서 원래 지면으로 부드럽게 잇습니다. */
function lateralScale(z: number): number {
  return 1 - curveProgress(
    Math.abs(z),
    ELEVATION_COURSE_ROAD_HALF_WIDTH,
    ELEVATION_COURSE_TERRAIN_HALF_WIDTH
  );
}

function lateralSlope(z: number): number {
  return (
    -curveDerivative(
      Math.abs(z),
      ELEVATION_COURSE_ROAD_HALF_WIDTH,
      ELEVATION_COURSE_TERRAIN_HALF_WIDTH
    ) * Math.sign(z)
  );
}

/**
 * 월드 좌표에서 지면 높이와 기울기를 샘플링합니다.
 *
 * `into`를 받는 이유는 주행/카메라 루프가 매 프레임 호출해도 임시 객체를 만들지 않게
 * 하기 위해서입니다. 도구나 일회성 호출은 생략해도 됩니다.
 */
export function sampleDrivingTerrain(
  x: number,
  z: number,
  into: DrivingTerrainSample = { height: 0, slopeX: 0, slopeZ: 0 }
): DrivingTerrainSample {
  const height = longitudinalHeight(x);
  const slopeX = longitudinalSlope(x);
  if (height === 0 && slopeX === 0) {
    into.height = 0;
    into.slopeX = 0;
    into.slopeZ = 0;
    return into;
  }

  const lateral = lateralScale(z);
  into.height = height * lateral;
  into.slopeX = slopeX * lateral;
  into.slopeZ = height * lateralSlope(z);
  return into;
}
